import React, { useContext, useEffect, useState } from 'react';
import pdfMake from 'pdfmake/build/pdfmake';
import * as PDFJS from 'pdfjs-dist/webpack';
import {
  Box,
  Button,
  Checkbox,
  Paper,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { MdThumbUp } from 'react-icons/md';
import { toast } from 'react-toastify';
import Toast from '../../../../../../components/Toast';
import { options } from '../../../../../../helpers/toastOptions';
import { useManager } from '../../../../../../hooks/Manager';
import { useModal } from '../../../../../../hooks/Modal';
import { usePickup } from '../../../../../../hooks/Pickup';
import { ModalTitle, ModalText } from './styled';
import { MyCollectionsContext } from '../..';
import { contentTag } from '../../../../../../reports/contentTag';
import { contentImageTag } from '../../../../../../reports/contentImageTag';

export const ModalPrintTag = () => {
  const [modalSelected, setModalSelected] = useState('modalPrintTag');
  const [isLoadingDetails, setIsLoadingDetails] = useState(true);
  const [selected, setSelected] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [disableDownloadTag, setDisableDownloadTag] = useState(true);
  const [imagesCropped, setImagesCropped] = useState([]);
  const [tagCount, setTagCount] = useState(0);

  const { getPickupManagerBranchDocumentation } = useManager();
  const { closeModal, Modal } = useModal();
  const { generateInvoiceTag } = usePickup();

  const {
    protocolToModal,
    setProtocolToModal,
    pickupIdToModal,
    setPickupIdToModal,
    branchParameterizationToModal,
    setBranchParameterizationToModal,
    modalMessage,
    setModalMessage,
    invoiceListToDownload,
    setInvoiceListToDownload,
    refreshSearch
  } = useContext(MyCollectionsContext);

  const readFileData = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        resolve(e.target.result);
      };
      reader.onerror = (err) => {
        reject(err);
      };
      reader.readAsDataURL(file);
    });
  };

  const convertPdfToImages = async (file) => {
    const images = [];
    const data = await readFileData(file);
    const pdf = await PDFJS.getDocument(data).promise;
    const canvas = document.createElement('canvas');

    for (let i = 0; i < pdf.numPages; i++) {
      // eslint-disable-next-line no-await-in-loop
      const page = await pdf.getPage(i + 1);
      const viewport = page.getViewport({ scale: 1 });
      const context = canvas.getContext('2d');
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      // eslint-disable-next-line no-await-in-loop
      await page.render({ canvasContext: context, viewport }).promise;
      images.push(canvas.toDataURL());
    }
    canvas.remove();
    return images;
  };

  const readImageData = (src) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });
  };

  const cropImageNow = (img) => {
    const image = readImageData(img).then((resolved) => {
      const width = 400;
      const height = 420;

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');

      const pixelRatio = window.devicePixelRatio;
      canvas.width = width * pixelRatio;
      canvas.height = height * pixelRatio;
      ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
      ctx.imageSmoothingQuality = 'high';

      ctx?.drawImage(resolved, 0, 0, width, height, 0, 0, width, height);

      const base64Image = canvas.toDataURL('image/png');

      return base64Image;
    });

    return image;
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = [];
      invoiceListToDownload.forEach((value) => {
        if (value.storageUrl && value.typeDocument === 'XML') {
          newSelected.push(value.taxInvoiceId);
        }
      });
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (_, taxInvoiceId) => {
    const selectedIndex = selected.indexOf(taxInvoiceId);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, taxInvoiceId);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover
    }
  }));

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14
    }
  }));

  const handleCloseModal = () => {
    setInvoiceListToDownload([]);
    setProtocolToModal('');
    setBranchParameterizationToModal('');
    closeModal();
  };

  const handleDownload = async () => {
    if (
      selected.length === 0 ||
      (selected.length === 1 && selected[0] === null)
    ) {
      toast(
        <Toast
          title="Atenção!"
          content="Selecione ao menos uma nota para imprimir a etiqueta."
          type="error"
        />,
        options
      );

      return;
    }

    try {
      setIsSubmitting(true);

      const invoicesId = selected?.map((value) => {
        const invoice = invoiceListToDownload.find(
          ({ taxInvoiceId }) => taxInvoiceId === value
        );

        return invoice.taxInvoiceId;
      });

      const params = {
        pickupId: pickupIdToModal,
        invoicesId
      };

      const invoiceDataResponse = await generateInvoiceTag({
        data: params
      });

      if (invoiceDataResponse.status === 200) {
        let tags = 0;
        let hasFailed = false;

        selected?.forEach((value) => {
          const invoice = invoiceListToDownload.find(
            ({ taxInvoiceId }) => taxInvoiceId === value
          );

          const invoiceFounded =
            invoiceDataResponse?.data?.data?.tagResponse?.find(
              ({ taxInvoiceId }) =>
                String(taxInvoiceId) === String(invoice.taxInvoiceId)
            );

          if (invoiceFounded) {
            tags += invoiceFounded?.tags.length;
          } else {
            hasFailed = true;
          }
        });

        if (hasFailed) {
          setIsSubmitting(false);

          toast(
            <Toast
              title="Atenção!"
              content="Falha ao imprimir etiqueta."
              type="error"
            />,
            options
          );
        }

        setTagCount(tags);

        selected?.forEach((value) => {
          const invoice = invoiceListToDownload.find(
            ({ taxInvoiceId }) => taxInvoiceId === value
          );

          const invoiceFounded =
            invoiceDataResponse?.data?.data?.tagResponse?.find(
              ({ taxInvoiceId }) =>
                String(taxInvoiceId) === String(invoice.taxInvoiceId)
            );

          if (invoiceFounded) {
            pdfMake
              .createPdf(
                contentTag({
                  tagData: [
                    {
                      ...invoiceFounded,
                      fileUrl: invoice?.storageUrl
                    }
                  ],
                  pickupData:
                    invoiceDataResponse?.data?.data?.pickupSearchResponse,
                  tagHasAlreadyPrinted: !!invoice?.generatedTag
                })
              )
              .getBlob(async (blob) => {
                const images = await convertPdfToImages(blob);

                if (images?.length > 0) {
                  // eslint-disable-next-line no-restricted-syntax
                  for await (const image of images) {
                    const newImage = await cropImageNow(image);

                    setImagesCropped((prev) => [...prev, newImage]);
                  }
                }
              });
          }
        });

        setIsSubmitting(false);

        // setPickupIdToModal();
        // refreshSearch();
        // closeModal();
      } else {
        throw new Error();
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);

      setIsSubmitting(false);

      toast(
        <Toast
          title="Atenção!"
          content="Somente NFs com Volumes e XML válido, estarão disponíveis para impressão de etiquetas."
          type="error"
        />,
        options
      );
    }
  };

  const isSelected = (name) => selected.indexOf(name) !== -1;

  const onComponentDidMount = async () => {
    if (invoiceListToDownload?.length === 0) {
      setModalMessage('Sem notas fiscais');
      return;
    }

    try {
      const documentationResponse = await getPickupManagerBranchDocumentation({
        branchParameterizationId: branchParameterizationToModal
      });

      if (documentationResponse?.length > 0) {
        const [downloadConfig] = documentationResponse;

        downloadConfig?.enableTagDownload && setDisableDownloadTag(false);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  useEffect(() => {
    if (imagesCropped.length > 0 && imagesCropped.length === tagCount) {
      pdfMake.createPdf(contentImageTag({ images: imagesCropped })).open();

      setPickupIdToModal();
      refreshSearch();
      closeModal();
    }
  }, [imagesCropped.length]);

  useEffect(() => {
    onComponentDidMount();
  }, []);

  return (
    <Modal preventClose>
      {modalSelected === 'modalPrintTag' && (
        <Stack spacing={2}>
          <ModalTitle>Impressão de Etiquetas</ModalTitle>

          <ModalText style={{ color: '#bb0000' }}>
            O número da NF será apresentado na etiqueta.
            <br />
            Atentar-se para que seja etiquetado corretamente os volumes
            <br />
            de cada NF, evitando extravios ou reentregas!
            <br />
            <br />
            Somente NFs com Volumes e XML válido, estarão disponíveis para
            impressão de etiquetas.
            <br />
            Em caso de divergência entrar em contato com Central de Coleta.
          </ModalText>

          <ModalText>
            Selecione as notas fiscais para fazer a impressão das etiquetas
          </ModalText>

          <ModalText>Protocolo: {protocolToModal}</ModalText>

          <TableContainer component={Paper}>
            <Table stickyHeader size="small">
              <TableHead>
                <StyledTableRow>
                  {!isLoadingDetails && invoiceListToDownload?.length > 0 && (
                    <StyledTableCell padding="checkbox">
                      <Checkbox
                        color="secondary"
                        disabled={
                          disableDownloadTag ||
                          isSubmitting ||
                          invoiceListToDownload?.filter(
                            (value) =>
                              value.storageUrl && value.typeDocument === 'XML'
                          )?.length === 0
                        }
                        indeterminate={
                          selected.length > 0 &&
                          selected.length <
                            invoiceListToDownload?.filter(
                              (value) =>
                                value.storageUrl && value.typeDocument === 'XML'
                            )?.length
                        }
                        checked={
                          invoiceListToDownload?.filter(
                            (value) =>
                              value.storageUrl && value.typeDocument === 'XML'
                          )?.length > 0 &&
                          selected.length ===
                            invoiceListToDownload?.filter(
                              (value) =>
                                value.storageUrl && value.typeDocument === 'XML'
                            )?.length
                        }
                        onChange={handleSelectAllClick}
                      />
                    </StyledTableCell>
                  )}
                  <StyledTableCell style={{ textAlign: 'center' }}>
                    Nota Fiscal
                  </StyledTableCell>
                  <StyledTableCell style={{ textAlign: 'center' }}>
                    Etiqueta Impressa?
                  </StyledTableCell>
                </StyledTableRow>
              </TableHead>
              <TableBody>
                {(isLoadingDetails || invoiceListToDownload?.length === 0) && (
                  <StyledTableRow>
                    <StyledTableCell
                      colSpan={3}
                      style={{ textAlign: 'center', color: '#888' }}
                    >
                      {modalMessage}
                    </StyledTableCell>
                  </StyledTableRow>
                )}
                {!isLoadingDetails && invoiceListToDownload?.length > 0 && (
                  <>
                    {invoiceListToDownload
                      ?.filter((value) => !!value.storageUrl)
                      ?.sort((a, b) => {
                        const invoiceNumbers = invoiceListToDownload.map(
                          (value) => value.number
                        );
                        const maxLength = String(
                          Math.max(...invoiceNumbers)
                        ).length;

                        const aPrinted = a.generatedTag === true ? '1' : '0';
                        const aValue = parseInt(
                          aPrinted + String(a.number).padStart(maxLength, '0'),
                          10
                        );
                        const bPrinted = b.generatedTag === true ? '1' : '0';
                        const bValue = parseInt(
                          bPrinted + String(b.number).padStart(maxLength, '0'),
                          10
                        );

                        if (aValue < bValue) {
                          return -1;
                        }
                        if (aValue > bValue) {
                          return 1;
                        }
                        return 0;
                      })
                      ?.map((row) => {
                        const isItemSelected = isSelected(row.taxInvoiceId);
                        const labelId = String(
                          // eslint-disable-next-line prefer-template
                          'table-checkbox-' + row.taxInvoiceId
                        );

                        return (
                          <StyledTableRow
                            key={row.taxInvoiceId}
                            onClick={(event) => {
                              if (
                                disableDownloadTag ||
                                isSubmitting ||
                                row.typeDocument !== 'XML'
                              ) {
                                return;
                              }
                              handleClick(event, row.taxInvoiceId);
                            }}
                            style={{
                              cursor:
                                disableDownloadTag ||
                                isSubmitting ||
                                row.typeDocument !== 'XML'
                                  ? 'default'
                                  : 'pointer'
                            }}
                          >
                            <StyledTableCell padding="checkbox">
                              <Checkbox
                                color="primary"
                                checked={isItemSelected}
                                inputProps={{ 'aria-labelledby': labelId }}
                                disabled={disableDownloadTag || isSubmitting}
                              />
                            </StyledTableCell>
                            <StyledTableCell style={{ textAlign: 'center' }}>
                              {row.number ?? '-'}
                            </StyledTableCell>
                            <StyledTableCell style={{ textAlign: 'center' }}>
                              <MdThumbUp
                                size={20}
                                style={{
                                  color: row?.generatedTag
                                    ? '#004e9a'
                                    : '#cccccc'
                                }}
                              />
                            </StyledTableCell>
                          </StyledTableRow>
                        );
                      })}
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: 32
            }}
          >
            <Button
              type="button"
              variant="contained"
              color="secondary"
              onClick={() => setModalSelected('modalConfirmClose')}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <LoadingButton
              type="button"
              variant="contained"
              onClick={handleDownload}
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              Confirmar
            </LoadingButton>
          </Box>
        </Stack>
      )}

      {modalSelected === 'modalConfirmClose' && (
        <Stack spacing={2}>
          <ModalTitle>Atenção</ModalTitle>

          <ModalText>
            Deseja realmente cancelar a impressão da etiqueta?
          </ModalText>

          <Box
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: 32
            }}
          >
            <Button
              type="button"
              variant="contained"
              color="attention"
              onClick={() => setModalSelected('modalPrintTag')}
            >
              Não
            </Button>
            <Button
              type="button"
              variant="contained"
              onClick={handleCloseModal}
            >
              Sim
            </Button>
          </Box>
        </Stack>
      )}
    </Modal>
  );
};
